import {
  useDistributorProfile,
  usePortfolioCategorySummary,
} from "creso-core/hooks";
import { FamilySource } from "creso-core/types";
import {
  buildFamilyProductBreakdownItems,
  findBreakdownByCategory,
  getPortfolioDistributionFromBreakdown,
  getAssetAllocationBucketsFromBreakdown,
} from "creso-core/utils";
import { useMemo } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { ProductBreakdown } from "../overview/product-breakdown";
import {
  ASSET_ALLOCATION_BUCKET_COLORS,
  AssetAllocation,
} from "../overview/asset-allocation";
import { PortfolioDistribution } from "../overview/portfolio-distribution";

function useFamilyPortfolioBreakdown(source: FamilySource) {
  const route = getRouteApi("/(auth)/_headed/clients/families/$id");
  const { family } = route.useLoaderData();
  const { data: portfolioBreakdown } = usePortfolioCategorySummary({
    familyId: family.id,
  });

  return useMemo(
    () => findBreakdownByCategory(portfolioBreakdown ?? [], source),
    [portfolioBreakdown, source]
  );
}

export function FamilyAssetAllocationSection({
  source,
}: {
  source: FamilySource;
}) {
  const breakdownBySource = useFamilyPortfolioBreakdown(source);

  const buckets = useMemo(
    () =>
      getAssetAllocationBucketsFromBreakdown(
        breakdownBySource,
        ASSET_ALLOCATION_BUCKET_COLORS
      ),
    [breakdownBySource]
  );

  const portfolioDistribution = useMemo(
    () => getPortfolioDistributionFromBreakdown(breakdownBySource),
    [breakdownBySource]
  );

  if (
    !breakdownBySource &&
    !buckets.length &&
    !portfolioDistribution.stocks.length &&
    !portfolioDistribution.sectors.length
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {buckets.length ? <AssetAllocation buckets={buckets} /> : null}
      {portfolioDistribution.stocks.length ||
      portfolioDistribution.sectors.length ? (
        <PortfolioDistribution {...portfolioDistribution} />
      ) : null}
    </div>
  );
}

export function FamilyProductsSection({ source }: { source: FamilySource }) {
  const breakdownBySource = useFamilyPortfolioBreakdown(source);
  const { data: profile } = useDistributorProfile();
  const products = useMemo(
    () => buildFamilyProductBreakdownItems(breakdownBySource, profile),
    [breakdownBySource, profile]
  );

  return <ProductBreakdown products={products} minProducts={2} />;
}
